import React, { useContext, useState, useEffect } from 'react';
import { Badge, Form } from 'react-bootstrap';
import ProjectContext from '../context/ProjectContext';
import AuthContext from '../context/AuthContext';
import DataTable from '../components/common/DataTable';
import SmartDiscoverySection from '../components/rules/SmartDiscoverySection';
import AdvancedRuleModal from '../components/rules/AdvancedRuleModal';
import RuleRow from '../components/rules/RuleRow';
import { useRules } from '../hooks/useRules';
import { useRuleForm } from '../hooks/useRuleForm';
import usePayees from '../hooks/usePayees';
import useClasses from '../hooks/useClasses';

const PL_TYPES = ['Income', 'Other Income', 'Cost of Goods Sold', 'Expenses', 'Other Expense'];
const BS_TYPES = ['Bank', 'Credit Card', 'Accounts receivable (A/R)', 'Accounts payable (A/P)', 'Other Current Assets', 'Other Current Liabilities', 'Fixed Assets', 'Long Term Liabilities', 'Equity'];

const Rules = () => {
  const { activeProject } = useContext(ProjectContext);
  const { user } = useContext(AuthContext);
  
  const {
    rules, suggestions, accounts, isEditor, loading,
    selectedSuggestion, setSelectedSuggestion,
    detailTransactions, fetchRules, fetchSuggestions,
    deleteRule, applyRule,
    focusAccount, setFocusAccount
  } = useRules(activeProject, user);

  const { payees, fetchPayees } = usePayees();
  const { classes, fetchClasses } = useClasses();

  useEffect(() => {
    if (activeProject) {
      fetchPayees(activeProject._id, { all: 'true' });
      fetchClasses(activeProject._id);
    }
  }, [activeProject, fetchPayees, fetchClasses]);

  const {
    showRuleModal, setShowRuleModal,
    editingRuleId,
    ruleFormData, setRuleFormData,
    previewTxs,
    openAdvancedRuleCreator,
    openEditRuleCreator,
    handleSaveAdvancedRule,
    handleUpdateRule,
    addCondition,
    removeCondition,
    updateCondition
  } = useRuleForm(activeProject, accounts, PL_TYPES, applyRule, fetchRules, fetchSuggestions, fetchPayees);

  const handleQuickApprove = async () => {
    if (!ruleFormData.targetAccountId) return alert('Select a category');
    try {
      const ruleRes = await axios.post(`/api/projects/${activeProject._id}/rules`, {
        name: `Rule for ${selectedSuggestion.description}`,
        conditionValue: selectedSuggestion.description,
        targetAccountId: ruleFormData.targetAccountId,
        transactionType: selectedSuggestion.totalAmount > 0 ? 'Money In' : 'Money Out'
      });
      await applyRule(ruleRes.data._id);
      setSelectedSuggestion(null);
      alert('Transactions categorized and rule saved!');
    } catch (err) { console.error(err); }
  };

  if (!activeProject) return <div className="p-5 text-center">Please select a company first.</div>;

  const tableHeaders = [
    { label: 'Name' }, { label: 'Conditions' }, { label: 'Target Category' }, { label: 'Action', align: 'right' }
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Rules & Automation</h1>
        <div className="d-flex align-items-center">
          <Form.Group className="mb-0 mr-3" style={{ minWidth: '220px' }}>
            <Form.Control 
              as="select" 
              size="sm" 
              value={focusAccount} 
              onChange={(e) => setFocusAccount(e.target.value)}
              className="border-primary"
            >
              <option value="">-- All Accounts --</option>
              {accounts.filter(acc => ['Bank', 'Credit Card'].includes(acc.type)).map(acc => (
                <option key={acc._id} value={acc._id}>{acc.name} ({acc.type})</option>
              ))}
            </Form.Control>
          </Form.Group>
          <button onClick={() => fetchSuggestions(focusAccount)} className="btn btn-outline-secondary btn-sm">
            <i className="fas fa-sync mr-1"></i> Refresh Discovery
          </button>
        </div>
      </div>
      
      {isEditor && (suggestions.length > 0 || loading) && (
        <SmartDiscoverySection 
          suggestions={suggestions}
          selectedSuggestion={selectedSuggestion}
          setSelectedSuggestion={setSelectedSuggestion}
          detailTransactions={detailTransactions}
          quickTargetId={ruleFormData.targetAccountId}
          setQuickTargetId={(val) => setRuleFormData({ ...ruleFormData, targetAccountId: val })}
          handleQuickApprove={handleQuickApprove}
          openAdvancedRuleCreator={() => openAdvancedRuleCreator(selectedSuggestion, ruleFormData.targetAccountId)}
          accounts={accounts}
          plTypes={PL_TYPES}
          loading={loading}
        />
      )}

      <div className="mb-4">
        <h5 className="mb-3 text-muted small text-uppercase font-weight-bold">Active Automation Rules</h5>
        <DataTable 
          headers={tableHeaders}
          data={rules}
          emptyMessage="No rules created yet."
          renderRow={(rule) => (
            <RuleRow 
              key={rule._id} 
              rule={rule} 
              payees={payees}
              classes={classes}
              isEditor={isEditor} 
              onDelete={deleteRule} 
              onEdit={openEditRuleCreator} 
            />
          )}
        />
      </div>

      <AdvancedRuleModal 
        show={showRuleModal}
        onHide={() => setShowRuleModal(false)}
        editingRuleId={editingRuleId}
        formData={ruleFormData}
        setFormData={setRuleFormData}
        onSave={editingRuleId ? handleUpdateRule : handleSaveAdvancedRule}
        accounts={accounts}
        payees={payees}
        classes={classes}
        plTypes={PL_TYPES}
        bsTypes={BS_TYPES}
        previewTxs={previewTxs}
        addCondition={addCondition}
        removeCondition={removeCondition}
        updateCondition={updateCondition}
      />
    </div>
  );
};

export default Rules;
